import type { Tab } from './index';
import { esc, filterTable, timeAgo } from '../utils';
import { fetchSheet, workerFetch } from '../worker';

interface FormDFiling {
  name: string; cik: string; fileDate: string; industry: string;
  fundType: string | null; totalOffer: number; totalSold: number | null;
  investors: number | null; jurisdiction: string; entityType: string;
  yearFounded: string | null; exemptions: string[]; isEquity: boolean;
  location: string; edgarUrl: string;
}
interface FormDResp { filings: FormDFiling[]; count: number; cachedAt: string; }

function fmtM(n: number | null): string {
  if (n == null) return '—';
  if (n >= 1e9)  return `$${(n/1e9).toFixed(1)}B`;
  if (n >= 1e6)  return `$${(n/1e6).toFixed(1)}M`;
  if (n >= 1e3)  return `$${(n/1e3).toFixed(0)}K`;
  return `$${n}`;
}

interface UnicornRow {
  Company?:        string;
  SubSector?:      string;
  Valuation?:      string;
  ValuationColor?: string;
  TotalRaised?:    string;
  LatestRound?:    string;
  LatestRoundDate?:string;
  LeadInvestors?:  string;
  RevenueEst?:     string;
  IPOOutlook?:     string;
  KeyProduct?:     string;
  WhyItMatters?:   string;
  RiskFactors?:    string;
  AlertSignals?:   string;
  Founded?:        string;
  Link1Label?:     string;
  Link1URL?:       string;
  Link2Label?:     string;
  Link2URL?:       string;
  Sector?:         string;
  Type?:           string;
}

const SECTOR_COLORS: Record<string, string> = {
  'Defense / Aerospace': '#60a5fa', 'AI / Compute': 'var(--accent)', 'Biotech / Health': '#34d399',
  'Fintech / RWA': 'var(--warn)', 'Space': '#a78bfa', 'Energy / Climate': '#fb923c',
  'Robotics': '#f472b6', 'Other': 'var(--text3)',
};
// Legacy seed: rows from before the Sector column existed. The curated set was all defense-tech;
// the one agent-flagged listed name (ABSI) is biotech. Explicit row.Sector always overrides this.
const LEGACY_SECTOR: Record<string, string> = { ABSI: 'Biotech / Health' };
function unicornType(r: UnicornRow): 'Private' | 'Listed' {
  if (r.Type) return r.Type.trim().toLowerCase() === 'listed' ? 'Listed' : 'Private';
  return (r.SubSector ?? '').toLowerCase().includes('listed') ? 'Listed' : 'Private';
}
function unicornSector(r: UnicornRow): string {
  if (r.Sector && r.Sector.trim()) return r.Sector.trim();
  if (r.Company && LEGACY_SECTOR[r.Company.toUpperCase()]) return LEGACY_SECTOR[r.Company.toUpperCase()];
  return unicornType(r) === 'Listed' ? 'Other' : 'Defense / Aerospace';
}
function sectorColor(s: string): string { return SECTOR_COLORS[s] ?? 'var(--text3)'; }

export class UnicornsTab implements Tab {
  readonly id = 'unicorns';
  private loaded    = false;
  private fdLoaded  = false;
  private activeTab: 'watchlist' | 'formd' = 'watchlist';
  private rows: UnicornRow[] = [];
  private activeSector = '';

  private el(id: string): HTMLElement {
    return document.getElementById(id) as HTMLElement;
  }

  init(): void {
    (this.el('unicorn-filter') as HTMLInputElement)
      .addEventListener('input', e => filterTable('unicorn-table', (e.target as HTMLInputElement).value));
    (this.el('unicorn-ipo-filter') as HTMLSelectElement)
      .addEventListener('change', e => this.filterIPO((e.target as HTMLSelectElement).value));

    document.getElementById('unicorn-tab-watchlist')?.addEventListener('click', () => this.switchTab('watchlist'));
    document.getElementById('unicorn-tab-formd')    ?.addEventListener('click', () => this.switchTab('formd'));
    document.getElementById('unicorn-fd-refresh')   ?.addEventListener('click', () => { this.fdLoaded = false; this.loadFormD(); });
  }

  render(): void {
    this.switchTab(this.activeTab);
  }

  private switchTab(tab: 'watchlist' | 'formd'): void {
    this.activeTab = tab;
    const isWatchlist = tab === 'watchlist';
    document.getElementById('unicorn-tab-watchlist')?.classList.toggle('active', isWatchlist);
    document.getElementById('unicorn-tab-formd')    ?.classList.toggle('active', !isWatchlist);
    const wPanel = document.getElementById('unicorn-watchlist-panel');
    const fPanel = document.getElementById('unicorn-formd-panel');
    if (wPanel) wPanel.style.display = isWatchlist ? '' : 'none';
    if (fPanel) fPanel.style.display = isWatchlist ? 'none' : '';
    // Show refresh button + updated label only on Form D tab
    const fdRefresh  = document.getElementById('unicorn-fd-refresh');
    const fdUpdated  = document.getElementById('unicorn-fd-updated');
    if (fdRefresh) fdRefresh.style.display  = isWatchlist ? 'none' : '';
    if (fdUpdated) fdUpdated.style.display  = isWatchlist ? 'none' : '';
    if (isWatchlist && !this.loaded) this.load();
    if (!isWatchlist && !this.fdLoaded) this.loadFormD();
  }

  private async load(): Promise<void> {
    const tbody = this.el('unicorn-tbody');
    tbody.innerHTML = `<tr><td colspan="13" style="text-align:center;color:var(--text3);padding:24px;letter-spacing:0.1em;font-size:11px;">LOADING…</td></tr>`;
    try {
      const data = await fetchSheet<UnicornRow>('Unicorns');
      this.rows = data.rows ?? [];
      if (!this.rows.length) {
        tbody.innerHTML = `<tr><td colspan="13" style="text-align:center;color:var(--text3);padding:24px;font-size:12px;">No data found in Unicorns sheet tab.</td></tr>`;
        return;
      }
      tbody.innerHTML = this.rows.map(r => this.buildRow(r)).join('');
      this.buildSectorPills();
      this.applySectorFilter();
      this.updateStats(this.rows);
      this.loaded = true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      tbody.innerHTML = `<tr><td colspan="13" style="text-align:center;padding:24px;">` +
        `<span style="color:var(--red);font-size:12px;">Could not load Unicorn Watchlist.</span><br>` +
        `<span style="color:var(--text3);font-size:10px;">${esc(msg)}</span></td></tr>`;
    }
  }

  private buildRow(r: UnicornRow): string {
    const links = [
      r.Link1Label && r.Link1URL ? `<a class="chart-link" href="${esc(r.Link1URL)}" target="_blank" rel="noopener">↗ ${esc(r.Link1Label)}</a>` : '',
      r.Link2Label && r.Link2URL ? `<a class="chart-link" href="${esc(r.Link2URL)}" target="_blank" rel="noopener">↗ ${esc(r.Link2Label)}</a>` : '',
    ].filter(Boolean).join('');

    const tags      = (r.SubSector ?? '').split('|').filter(Boolean).map(t => `<span class="tag">${esc(t.trim())}</span>`).join('');
    const investors = (r.LeadInvestors ?? '').split('|').map(i => esc(i.trim())).join('<br>');
    const valColor  = r.ValuationColor === 'warn' ? 'var(--warn)' : 'var(--green)';
    const ipoClass  = (r.IPOOutlook ?? '').startsWith('Likely') ? 'status-upcoming' : 'status-pipeline';
    const sec = unicornSector(r);
    const typ = unicornType(r);
    const tCol = typ === 'Listed' ? 'var(--accent)' : '#60a5fa';
    const typeBadge  = `<span class="tag" style="background:${tCol}22;color:${tCol};font-size:9px;">${typ}</span>`;
    const sectorChip = `<span class="tag" style="background:${sectorColor(sec)}22;color:${sectorColor(sec)};">${esc(sec)}</span>`;

    return `<tr data-sector="${esc(sec)}">
      <td><strong style="color:var(--accent);font-size:13px;">${esc(r.Company ?? '—')}</strong> ${typeBadge}${links ? `<div class="quick-links">${links}</div>` : ''}</td>
      <td>${sectorChip}${tags}</td>
      <td><strong style="color:${valColor};">${esc(r.Valuation ?? '—')}</strong></td>
      <td class="text-muted">${esc(r.TotalRaised ?? '—')}</td>
      <td><strong>${esc(r.LatestRound ?? '—')}</strong><div class="text-dim text-small">${esc(r.LatestRoundDate ?? '')}</div></td>
      <td class="text-muted">${investors}</td>
      <td class="text-muted">${esc(r.RevenueEst ?? '—')}</td>
      <td><span class="status-badge ${ipoClass}">${esc(r.IPOOutlook ?? '—')}</span></td>
      <td class="thesis-cell">${esc(r.KeyProduct ?? '—')}</td>
      <td class="thesis-cell">${esc(r.WhyItMatters ?? '—')}</td>
      <td class="risk-cell">${esc(r.RiskFactors ?? '—')}</td>
      <td class="notes-cell" style="color:var(--warn);">${esc(r.AlertSignals ?? '—')}</td>
      <td class="text-muted">${esc(r.Founded ?? '—')}</td>
    </tr>`;
  }

  private buildSectorPills(): void {
    const panel = document.getElementById('unicorn-watchlist-panel');
    if (!panel) return;
    let bar = document.getElementById('unicorn-sector-pills');
    if (!bar) {
      bar = document.createElement('div');
      bar.id = 'unicorn-sector-pills';
      bar.style.cssText = 'display:flex;flex-wrap:wrap;gap:6px;margin:0 0 12px;';
      panel.insertBefore(bar, panel.firstChild);
    }
    const counts = new Map<string, number>();
    this.rows.forEach(r => { const s = unicornSector(r); counts.set(s, (counts.get(s) ?? 0) + 1); });
    const sectors = [...counts.keys()].sort();
    const pill = (label: string, sec: string, n: number) => {
      const active = this.activeSector === sec;
      const col = sec ? sectorColor(sec) : 'var(--text)';
      return `<button class="unicorn-pill" data-sec="${esc(sec)}" style="cursor:pointer;font-family:var(--mono);` +
        `font-size:11px;padding:4px 10px;border-radius:12px;border:1px solid ${active ? col : 'var(--border)'};` +
        `background:${active ? col + '22' : 'transparent'};color:${active ? col : 'var(--text3)'};">` +
        `${esc(label)} <span style="opacity:0.6;">${n}</span></button>`;
    };
    bar.innerHTML = pill('All', '', this.rows.length) + sectors.map(s => pill(s, s, counts.get(s) ?? 0)).join('');
    bar.querySelectorAll<HTMLButtonElement>('.unicorn-pill').forEach(b =>
      b.addEventListener('click', () => { this.activeSector = b.dataset.sec ?? ''; this.buildSectorPills(); this.applySectorFilter(); }));
  }

  private applySectorFilter(): void {
    document.querySelectorAll<HTMLElement>('#unicorn-table tbody tr').forEach(tr => {
      const ok = !this.activeSector || tr.getAttribute('data-sector') === this.activeSector;
      tr.style.display = ok ? '' : 'none';
    });
  }

  private filterIPO(val: string): void {
    document.querySelectorAll<HTMLElement>('#unicorn-table tbody tr').forEach(row => {
      row.style.display = !val || row.textContent?.includes(val) ? '' : 'none';
    });
  }

  private updateStats(rows: UnicornRow[]): void {
    const countEl = document.getElementById('unicorn-stat-count');
    const likelyEl = document.getElementById('unicorn-stat-likely');
    if (countEl) countEl.textContent = String(rows.length);
    if (likelyEl) likelyEl.textContent = String(rows.filter(r => (r.IPOOutlook ?? '').startsWith('Likely')).length);
  }

  private async loadFormD(): Promise<void> {
    const container = document.getElementById('unicorn-fd-body');
    if (!container) return;
    container.innerHTML = `<div style="padding:20px;color:var(--text3);font-family:var(--mono);font-size:12px;">Fetching recent Form D filings from SEC EDGAR…</div>`;
    try {
      const data = await workerFetch<FormDResp>('/api/form-d');
      this.fdLoaded = true;
      const updEl = document.getElementById('unicorn-fd-updated');
      if (updEl) updEl.textContent = `Cached ${timeAgo(data.cachedAt)} · ${data.count} filings`;
      this.renderFormD(data.filings);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      container.innerHTML = `<div style="padding:20px;color:var(--red);font-family:var(--mono);font-size:12px;">Error: ${esc(msg)}</div>`;
    }
  }

  private renderFormD(filings: FormDFiling[]): void {
    const container = document.getElementById('unicorn-fd-body');
    if (!container) return;
    if (!filings.length) {
      container.innerHTML = `<div class="empty-state">No qualifying Form D filings found in the last 30 days.</div>`;
      return;
    }

    const indCol: Record<string, string> = {
      'Technology': 'var(--accent)', 'Health Care': '#f472b6', 'Finance': 'var(--warn)',
      'Biotechnology': '#34d399', 'Energy': '#fb923c', 'Real Estate': '#60a5fa',
    };

    const rows = filings.map(f => {
      const col  = indCol[f.industry] ?? 'var(--text3)';
      const pct  = f.totalSold && f.totalOffer ? Math.round((f.totalSold / f.totalOffer) * 100) : null;
      const isVC = f.fundType != null;

      return `
        <tr style="border-bottom:1px solid var(--border);">
          <td style="padding:10px 12px;">
            <div style="font-weight:700;color:var(--text);margin-bottom:2px;">${esc(f.name)}</div>
            <a href="${esc(f.edgarUrl)}" target="_blank" rel="noopener" style="font-size:10px;color:var(--text3);">EDGAR ↗</a>
          </td>
          <td style="padding:10px 12px;">
            <span style="background:rgba(255,255,255,0.05);border:1px solid var(--border);border-radius:2px;font-size:10px;padding:1px 5px;color:${col};">${esc(f.industry)}</span>
            ${isVC ? `<div style="font-size:10px;color:var(--text3);margin-top:2px;">${esc(f.fundType ?? '')}</div>` : ''}
          </td>
          <td style="padding:10px 12px;font-family:var(--mono);font-size:12px;">
            <div style="font-weight:700;color:var(--accent);">${fmtM(f.totalSold)}</div>
            <div style="font-size:10px;color:var(--text3);">of ${fmtM(f.totalOffer)}</div>
            ${pct != null ? `<div style="height:3px;width:${Math.min(pct,100)}%;background:var(--accent);border-radius:2px;margin-top:3px;"></div>` : ''}
          </td>
          <td style="padding:10px 12px;font-family:var(--mono);font-size:12px;color:var(--text3);">${f.investors ?? '—'}</td>
          <td style="padding:10px 12px;font-family:var(--mono);font-size:11px;color:var(--text3);">${esc(f.location || f.jurisdiction)}</td>
          <td style="padding:10px 12px;font-family:var(--mono);font-size:11px;color:var(--text3);">${esc(f.fileDate)}</td>
          <td style="padding:10px 12px;font-size:11px;">
            ${f.yearFounded ? `<span style="color:var(--text3);">Founded ${esc(f.yearFounded)}</span>` : ''}
            ${f.exemptions.includes('06c') ? `<div style="color:var(--warn);font-size:10px;">506(c) Public Advert.</div>` : ''}
          </td>
        </tr>`;
    }).join('');

    container.innerHTML = `
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead>
          <tr style="border-bottom:2px solid var(--border);">
            <th style="padding:7px 12px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Company</th>
            <th style="padding:7px 12px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Sector</th>
            <th style="padding:7px 12px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Raised / Target</th>
            <th style="padding:7px 12px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Investors</th>
            <th style="padding:7px 12px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Location</th>
            <th style="padding:7px 12px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Filed</th>
            <th style="padding:7px 12px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Notes</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <div style="font-size:10px;color:var(--text3);text-align:right;padding:8px 2px;margin-top:4px;">
        Source: SEC EDGAR Form D · Reg D equity raises last 30 days · sorted by amount raised · 6-hour cache
      </div>`;
  }
}
